import React, { createContext, useState, useContext, useEffect, useCallback, useMemo } from 'react';

const AppModeContext = createContext(null);

// Available app modes
export const APP_MODES = {
  VISITOR: 'visitor',
  MEMBER: 'member',
  LEADER: 'leader', // Leader role now includes all utilisateur features
  DG: 'dg' // Director General - admin only
};

export const useAppMode = () => {
  const context = useContext(AppModeContext);
  if (!context) {
    throw new Error('useAppMode must be used within an AppModeProvider');
  }
  return context;
};

export const AppModeProvider = ({ children }) => {
  const [appMode, setAppModeState] = useState(() => {
    // Initialize from localStorage
    const stored = localStorage.getItem('pcc_app_mode');
    return stored || APP_MODES.VISITOR;
  });
  
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Persist mode changes to localStorage
  useEffect(() => {
    localStorage.setItem('pcc_app_mode', appMode);
  }, [appMode]);

  // Set app mode with transition
  const setAppMode = useCallback((mode) => {
    if (Object.values(APP_MODES).includes(mode)) {
      setIsTransitioning(true);
      // Small delay for smooth transition
      setTimeout(() => {
        setAppModeState(mode);
        setIsTransitioning(false);
      }, 100);
    } else {
      console.error(`Invalid app mode: ${mode}`);
    }
  }, []);

  // Switch to visitor mode
  const switchToVisitor = useCallback(() => {
    setAppMode(APP_MODES.VISITOR);
  }, [setAppMode]);

  // Switch to member mode
  const switchToMember = useCallback(() => {
    setAppMode(APP_MODES.MEMBER);
  }, [setAppMode]);

  // Switch to leader mode
  const switchToLeader = useCallback(() => {
    setAppMode(APP_MODES.LEADER);
  }, [setAppMode]);

  // Switch to DG mode (admin only)
  const switchToDG = useCallback(() => {
    setAppMode(APP_MODES.DG);
  }, [setAppMode]);

  // Check current mode
  const isVisitorMode = appMode === APP_MODES.VISITOR;
  const isMemberMode = appMode === APP_MODES.MEMBER;
  const isLeaderMode = appMode === APP_MODES.LEADER;
  const isDGMode = appMode === APP_MODES.DG;

  // Get mode display name in French
  const getModeDisplayName = useCallback((mode = appMode) => {
    switch (mode) {
      case APP_MODES.VISITOR:
        return 'Visiteur';
      case APP_MODES.MEMBER:
        return 'Membre';
      case APP_MODES.LEADER:
        return 'Responsable';
      case APP_MODES.DG:
        return 'Directeur Général';
      default:
        return 'Visiteur';
    }
  }, [appMode]);

  // Reset mode on logout
  const resetMode = useCallback(() => {
    setAppModeState(APP_MODES.VISITOR);
    localStorage.removeItem('pcc_app_mode');
  }, []);

  const value = useMemo(() => ({
    appMode,
    setAppMode,
    switchToVisitor,
    switchToMember,
    switchToLeader,
    switchToDG,
    isVisitorMode,
    isMemberMode,
    isLeaderMode,
    isDGMode,
    getModeDisplayName,
    resetMode,
    isTransitioning,
    APP_MODES
  }), [appMode, setAppMode, switchToVisitor, switchToMember, switchToLeader, switchToDG,
    isVisitorMode, isMemberMode, isLeaderMode, isDGMode, getModeDisplayName, resetMode, isTransitioning]);

  return (
    <AppModeContext.Provider value={value}>
      {children}
    </AppModeContext.Provider>
  );
};

export default AppModeContext;
